import * as React from "react";
import { useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import styled, { css } from "styled-components";
import { Select, Spinner, Icon, ConfirmDialog, Tooltip } from "components/common";
import { Button, ButtonToolbar, ControlLabel, HelpBlock } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useModelProviders from "ai-management/hooks/useModelProviders";
import useModels from "ai-management/hooks/useModels";
import useModelProviderMutations from "ai-management/hooks/useModelProviderMutations";
import ProviderCredentialFields from "ai-management/components/ProviderCredentialFields";
import IGNORE_AUTOFILL from "ai-management/components/passwordManagerAutofill";
import { emptyAuthFor, toFormAuth } from "ai-management/providerAuth";
import FormGroup from "components/bootstrap/FormGroup";
const secretUsable = (value) => !!(value && (value.set_value || value.keep_value));
const credentialsOf = (values) => {
  var _a;
  return (_a = values.credentials) != null ? _a : { provider: values.provider };
};
const isOllama = (values) => {
  var _a;
  return ((_a = credentialsOf(values).auth) == null ? void 0 : _a.type) === "ollama";
};
const ollamaBlocker = (values) => {
  const auth = credentialsOf(values).auth;
  if ((auth == null ? void 0 : auth.type) !== "ollama") {
    return null;
  }
  if (!auth.base_url) {
    return "Enter the base URL of your Ollama server.";
  }
  if (values.baseUrlAllowlisted === void 0) {
    return "Checking the base URL against the URL allowlist\u2026";
  }
  return values.baseUrlAllowlisted ? null : "Add the base URL to the URL allowlist.";
};
const modelPlaceholder = (values, loading, credentials) => {
  if (!values.provider) {
    return "Select a provider first";
  }
  if (!credentials) {
    return isOllama(values) ? "Enter an allowlisted base URL to load models" : "Enter credentials to load models";
  }
  return loading ? "Loading models\u2026" : "Select a model";
};
const modelHelp = (values, loaded) => {
  var _a;
  if (isOllama(values)) {
    return (_a = ollamaBlocker(values)) != null ? _a : "Models this Ollama has pulled. An empty list means it has none.";
  }
  return loaded ? "Models this account can use, as reported by the provider." : "Enter credentials below to load the models available to your account.";
};
const hasUsableCredential = (values) => {
  const auth = credentialsOf(values).auth;
  switch (auth == null ? void 0 : auth.type) {
    case "api_key":
      return secretUsable(auth.api_key);
    case "bedrock_api_token":
      return secretUsable(auth.token) && !!auth.region;
    case "bedrock_aws_credentials":
      return secretUsable(auth.access_key_id) && secretUsable(auth.secret_key) && !!auth.region;
    // Ollama has no secret at all: the URL is the whole configuration, and it has to be allowlisted.
    case "ollama":
      return ollamaBlocker(values) === null;
    default:
      return false;
  }
};
const saveBlockedReason = (values) => {
  var _a;
  const auth = credentialsOf(values).auth;
  if (!values.provider) {
    return "Select a provider.";
  }
  if ((auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" && secretUsable(auth.access_key_id) !== secretUsable(auth.secret_key)) {
    return "Enter both the access key ID and the secret access key, or clear both.";
  }
  if (((auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" || (auth == null ? void 0 : auth.type) === "bedrock_api_token") && !auth.region) {
    return "Select an AWS region. Bedrock model lists and endpoints are specific to one region.";
  }
  if (!hasUsableCredential(values)) {
    return (_a = ollamaBlocker(values)) != null ? _a : "Enter credentials for this provider.";
  }
  if (!values.default_model_id) {
    return "Select a model.";
  }
  return null;
};
const ErrorBlock = styled.pre`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
const TestStatus = styled.span`
  margin-left: ${({ theme }) => theme.spacings.sm};
`;
const SuccessIcon = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.variant.success};
`
);
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const SaveHintIcon = styled(Icon)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xxs};
  `
);
const DangerZone = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.lg};
    padding-top: ${theme.spacings.md};
    border-top: 1px solid ${theme.colors.variant.lightest.default};
  `
);
const DangerZoneTitle = styled.h4(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.sm};
    font-size: ${theme.fonts.size.body};
    font-weight: 600;
  `
);
const FormContents = ({ providers, initialConfig }) => {
  var _a;
  const sendTelemetry = useSendTelemetry();
  const { remove, test } = useModelProviderMutations();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const credentialsForLookup = hasUsableCredential(values) ? credentialsOf(values) : void 0;
  const {
    data: models,
    isFetching: modelsLoading,
    error: modelsError
  } = useModels(values.provider, credentialsForLookup);
  const [testResult, setTestResult] = useState(null);
  const isConfigured = !!((initialConfig == null ? void 0 : initialConfig.default_provider) && (initialConfig == null ? void 0 : initialConfig.default_model_id));
  const testPayload = useMemo(
    () => ({
      provider: values.provider,
      model_id: values.default_model_id,
      credentials: values.credentials
    }),
    [values.provider, values.default_model_id, values.credentials]
  );
  const confirmDelete = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.MODEL_PROVIDER_CONFIG_DELETED, {
      app_section: "aiagent-model-provider",
      event_details: { provider: initialConfig.default_provider }
    });
    remove.mutateAsync().then(() => setShowDeleteConfirm(false)).catch(() => {
    });
  };
  const storedAuth = (_a = initialConfig.providers.find((entry) => entry.provider === values.provider)) == null ? void 0 : _a.auth;
  const isEditing = !!(initialConfig.default_provider && initialConfig.default_model_id);
  const submitButtonText = isEditing ? "Update provider" : "Create provider";
  const submitLoadingText = isEditing ? "Updating provider..." : "Creating provider...";
  const onProviderChange = (provider) => {
    setFieldValue("provider", provider);
    setFieldValue("credentials", { provider, auth: emptyAuthFor(provider) });
    setFieldValue("default_model_id", "");
    setFieldValue("baseUrlAllowlisted", void 0);
    setTestResult(null);
  };
  const onTest = () => {
    setTestResult(null);
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.MODEL_PROVIDER_CONFIG_TEST_CLICKED, {
      app_section: "aiagent-model-provider",
      event_details: { provider: values.provider }
    });
    test.mutateAsync(testPayload).then((result) => setTestResult(result)).catch((error) => setTestResult({ success: false, message: String(error) }));
  };
  const saveHint = saveBlockedReason(values);
  const incomplete = !!saveHint;
  const testDisabled = incomplete || test.isPending;
  const submitDisabled = incomplete;
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "provider" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Provider"), /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "provider",
      placeholder: "Select a provider",
      options: providers.map((provider) => ({ value: provider.id, label: provider.display })).sort((a, b) => a.label.localeCompare(b.label)),
      value: values.provider,
      onChange: onProviderChange,
      clearable: false,
      inputProps: IGNORE_AUTOFILL
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "The LLM provider the agents should use.")), values.provider && /* @__PURE__ */ React.createElement(ProviderCredentialFields, { key: values.provider, provider: values.provider, storedAuth }), values.provider && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "model" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Model"), /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "model",
      placeholder: modelPlaceholder(values, modelsLoading, credentialsForLookup),
      options: models.map((model) => ({ value: model.id, label: model.name })),
      value: values.default_model_id,
      onChange: (modelId) => {
        setFieldValue("default_model_id", modelId);
        setTestResult(null);
      },
      disabled: !values.provider || !credentialsForLookup || modelsLoading || models.length === 0,
      inputProps: IGNORE_AUTOFILL
    }
  ), modelsError ? (
    // The provider refused or could not be reached. Shown rather than swallowed: an empty
    // dropdown with no explanation reads as "no models supported", which is never the reason.
    /* @__PURE__ */ React.createElement(ErrorBlock, null, modelsError.message)
  ) : /* @__PURE__ */ React.createElement(HelpBlock, null, modelHelp(values, !!credentialsForLookup))), testResult && !testResult.success && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Test error"), /* @__PURE__ */ React.createElement(ErrorBlock, null, testResult.message)), isConfigured && /* @__PURE__ */ React.createElement(DangerZone, null, /* @__PURE__ */ React.createElement(DangerZoneTitle, null, "Danger Zone"), /* @__PURE__ */ React.createElement(HelpBlock, null, "Deleting the model provider configuration stops every agent workflow from running until a new provider is configured."), /* @__PURE__ */ React.createElement(Button, { type: "button", bsStyle: "danger", disabled: remove.isPending, onClick: () => setShowDeleteConfirm(true) }, "Delete provider")), /* @__PURE__ */ React.createElement(Actions, null, /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: onTest, disabled: testDisabled }, test.isPending ? /* @__PURE__ */ React.createElement(Spinner, { delay: 0, text: "Testing" }) : "Test", (testResult == null ? void 0 : testResult.success) && /* @__PURE__ */ React.createElement(TestStatus, null, /* @__PURE__ */ React.createElement(SuccessIcon, { name: "check_circle" }))), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      type: "submit",
      "aria-label": submitButtonText,
      disabled: submitDisabled || isSubmitting
    },
    isSubmitting ? /* @__PURE__ */ React.createElement(Spinner, { text: submitLoadingText, delay: 0 }) : /* @__PURE__ */ React.createElement(React.Fragment, null, submitButtonText, saveHint && /* @__PURE__ */ React.createElement(Tooltip, { label: saveHint, position: "top" }, /* @__PURE__ */ React.createElement(SaveHintIcon, { name: "info", "data-testid": "save-hint" })))
  ))), showDeleteConfirm && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: true,
      title: "Delete model provider configuration",
      btnConfirmText: "Delete provider",
      isAsyncSubmit: true,
      isSubmitting: remove.isPending,
      submitLoadingText: "Deleting provider...",
      onConfirm: confirmDelete,
      onCancel: () => setShowDeleteConfirm(false)
    },
    "Are you sure you want to delete the configured model provider? Agents will not run until a new provider is configured."
  ));
};
const ModelProviderConfigForm = ({ initialConfig }) => {
  var _a;
  const sendTelemetry = useSendTelemetry();
  const { data: providers } = useModelProviders();
  const { save } = useModelProviderMutations();
  const defaultProvider = initialConfig.default_provider;
  const storedEntry = initialConfig.providers.find((entry) => entry.provider === defaultProvider);
  const initialValues = {
    provider: defaultProvider,
    default_model_id: (_a = initialConfig.default_model_id) != null ? _a : "",
    // The stored auth object, so the form starts on the method the operator chose. Secrets are managed by
    // the credential fields via their is_set flags and are never sent back as plaintext.
    credentials: {
      provider: defaultProvider,
      auth: (storedEntry == null ? void 0 : storedEntry.auth) ? toFormAuth(storedEntry.auth) : emptyAuthFor(defaultProvider)
    },
    baseUrlAllowlisted: void 0
  };
  const onSubmit = (values) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.MODEL_PROVIDER_CONFIG_SAVED, {
      app_section: "aiagent-model-provider",
      event_details: { provider: values.provider }
    });
    return save.mutateAsync({
      // A credentials entry belongs to a provider, so there is nothing to send until one is chosen.
      providers: values.provider ? [values.credentials] : [],
      default_provider: values.provider,
      default_model_id: values.default_model_id
    });
  };
  return /* @__PURE__ */ React.createElement(Formik, { initialValues, enableReinitialize: true, onSubmit }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(FormContents, { providers, initialConfig })));
};
export default ModelProviderConfigForm;
